"use client";

import { getAnalyticsRegion } from "./get-analytics-region";
import { triggerAnalyticsEvent } from "./trigger-analytics-event";
import { TrackingAction, TrackingType } from "./types";

const REGION_ATTR = "data-analytics-region";
const ID_ATTR = "data-analytics-id";
const DEFAULT_UNLABELLED_ANALYTICS_ID =
  "sec_visibility_tracker_region_default_unlabelled_id";
const OBSERVED_ATTR = "data-region-observed";

let observer: IntersectionObserver | null = null;
let mutationObserver: MutationObserver | null = null;
let mutationDebounceTimer: ReturnType<typeof setTimeout> | null = null;

function handleIntersection(entries: IntersectionObserverEntry[]) {
  for (const entry of entries) {
    if (!entry.isIntersecting) continue;

    const el = entry.target as HTMLElement;
    const region = el.getAttribute(REGION_ATTR);

    if (!region) continue;

    // Build the full region path including this element's own region
    // and its ancestor regions (e.g. "page > hero").
    // getAnalyticsRegion starts from parentElement (designed for click
    // targets inside a region), so we append the element's own region.
    const ancestorRegion = getAnalyticsRegion(el);
    const text = ancestorRegion ? `${ancestorRegion} > ${region}` : region;

    const analyticsLabel = el.getAttribute("data-analytics-label");
    const raw = (analyticsLabel ?? el.innerText)?.replace(/\s+/g, " ").trim();
    const label = raw
      ? raw.length > 25
        ? `${raw.slice(0, 25).trim()}…`
        : raw
      : region;

    const analyticsId = el.getAttribute(ID_ATTR);

    triggerAnalyticsEvent({
      location: text,
      text: label,
      analyticsId: analyticsId ?? DEFAULT_UNLABELLED_ANALYTICS_ID,
      action: TrackingAction.Visible,
      type: TrackingType.Element,
    });

    // Fire once per element, then stop watching it
    el.setAttribute(OBSERVED_ATTR, "true");
    observer?.unobserve(el);
  }
}

function observeRegions() {
  if (!observer) return;

  const elements = document.querySelectorAll<HTMLElement>(
    `[${REGION_ATTR}]:not([${OBSERVED_ATTR}])`,
  );

  for (const el of elements) {
    observer.observe(el);
  }
}

/**
 * Starts a single global IntersectionObserver that watches all elements
 * with `data-analytics-region` and fires an "Element Visible" analytics
 * event when each scrolls into view (once per element).
 *
 * Also watches for dynamically added regions via MutationObserver.
 *
 * Call `stopRegionVisibilityObserver()` to tear down.
 */
export function startRegionVisibilityObserver() {
  if (typeof window === "undefined") return;
  if (observer) return; // Already running

  observer = new IntersectionObserver(handleIntersection, {
    threshold: 0,
  });

  // Observe all existing regions
  observeRegions();

  // Watch for dynamically added regions (e.g. lazy-loaded sections),
  // debounced to avoid re-querying on every DOM mutation.
  mutationObserver = new MutationObserver(() => {
    if (mutationDebounceTimer) clearTimeout(mutationDebounceTimer);
    mutationDebounceTimer = setTimeout(observeRegions, 100);
  });

  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

export function stopRegionVisibilityObserver() {
  observer?.disconnect();
  observer = null;
  mutationObserver?.disconnect();
  mutationObserver = null;
  if (mutationDebounceTimer) clearTimeout(mutationDebounceTimer);
  mutationDebounceTimer = null;

  // Clean up observed markers so a restart can re-observe
  const observed = document.querySelectorAll(`[${OBSERVED_ATTR}]`);
  for (const el of observed) {
    el.removeAttribute(OBSERVED_ATTR);
  }
}
